import { always, and, eq, inList, type Predicate, type SearchEngine } from '@shared/core/search';
import { ORDER_OF_TYPE } from '@shared/core/search/entities/order';

import type { SearchDocument } from '../../entities/search';
import { getCollectionConfig } from '../constants/collections';
import { getEntitledSections, getSectionConfig, type SearchSection, UMBRELLA_SECTIONS } from '../constants/sections';
import type { Filter } from './filters';

// Umbrella type key (`orders`/`bills`) → its collection, so an umbrella token expands
// to every entitled section of that collection instead of matching a section key that
// never exists (the four order sections are keyed `sales-orders` etc, never `orders`).
const UMBRELLA_COLLECTION_BY_KEY = new Map(UMBRELLA_SECTIONS.map((section) => [section.key, section.collection]));

/**
 * Pure composition of the ⌘K palette's two axes into search legs.
 *
 *   SCOPE (depth)   ─▶ which ONE section renders (`resolveQuerySections`)
 *   type filter     ─▶ which sections' legs are issued (breadth, OR ACROSS LEGS)
 *   lifecycle/status─▶ within-leg `filter_by` clauses (AND inside each leg)
 *
 * TYPE = LEG SELECTION, never a merged `filter_by`. Each section keeps its own leg
 * pinned to its discriminator, guaranteeing every type a slot — so `so:` + `po:`
 * issue two `orders` legs (SALES_ORDER, PURCHASE_ORDER), NOT one merged
 * `orderType:=[SALES_ORDER,PURCHASE_ORDER]` leg. Cross-collection type chips are
 * naturally separate legs; the OR is realised across legs in both cases.
 */

/** Which sections' legs to issue. Precedence: scope > typeFacet > typed type pills. */
export function resolveQuerySections(
  entitledSections: readonly SearchSection[],
  scope: string | null,
  filters: readonly Filter[],
  typeFacet: string | null = null
): SearchSection[] {
  if (scope !== null) {
    const inEntitled = entitledSections.find((section) => section.key === scope);
    if (inEntitled) return [inEntitled];
    // A scope is only set to a section the palette can render; fall back to the
    // registry so an umbrella/derived scope still resolves to a single leg.
    const config = getSectionConfig(scope);
    return config ? [config] : [];
  }

  const typeKeys =
    typeFacet !== null
      ? [typeFacet]
      : filters.filter((f): f is Extract<Filter, { dim: 'type' }> => f.dim === 'type').map((f) => f.section);
  if (typeKeys.length === 0) return [...entitledSections];
  // A facetable type key selects its own section; an umbrella key (`orders`/`bills`)
  // selects every entitled section of its collection — so `order:` fans out to all four
  // order sections and `bill:` to both bill sections.
  const wantedSectionKeys = new Set(typeKeys.filter((key) => !UMBRELLA_COLLECTION_BY_KEY.has(key)));
  const wantedCollections = new Set(typeKeys.map((key) => UMBRELLA_COLLECTION_BY_KEY.get(key)).filter(Boolean));
  return entitledSections.filter(
    (section) => wantedSectionKeys.has(section.key) || wantedCollections.has(section.collection)
  );
}

/**
 * The filter for one section leg: collection scope AND discriminator AND lifecycle
 * AND status, in that order. `always()` when no clause applies.
 *   - scope         : the collection's standing clause on this engine (business
 *                     entities hide disabled rows; Algolia orders and production
 *                     runs hide templates), from its UI config.
 *   - discriminator : the section's pinned `orderType`/`billType`, if any.
 *   - lifecycle     : union of the `is:open`/`is:closed` status sets for THIS
 *                     section (both filters ⇒ open set then closed set).
 *   - status        : the scoped status sub-facet — only when scoped, the section
 *                     has a status field, and the value is a live bucket (the
 *                     status-injection guard; `is:open`/`is:closed` bypass it).
 */
export function buildLegFilter(params: {
  section: SearchSection;
  filters: readonly Filter[];
  scoped: boolean;
  liveStatusValues: readonly string[];
  hasStatus: boolean;
  engine: SearchEngine;
}): Predicate<SearchDocument> {
  const { section, filters, scoped, liveStatusValues, hasStatus, engine } = params;
  const statusFilter = filters.find((f): f is Extract<Filter, { dim: 'status' }> => f.dim === 'status');
  const legalStatus =
    scoped && hasStatus && statusFilter && liveStatusValues.includes(statusFilter.value) ? statusFilter.value : null;

  return and([
    sectionBaseFilter(section, engine),
    buildLifecycleFilter(section, filters),
    legalStatus === null ? always<SearchDocument>() : eq<SearchDocument>('status', legalStatus),
  ]);
}

/**
 * The clauses every leg of a section carries whatever the user filters on: the
 * collection's standing scope on this engine AND the section discriminator. The
 * facet-only companion leg sends exactly this, so its buckets count the same rows
 * the results leg can show.
 */
export function sectionBaseFilter(section: SearchSection, engine: SearchEngine): Predicate<SearchDocument> {
  return and([
    getCollectionConfig(section.collection)?.scope?.(engine) ?? always<SearchDocument>(),
    discriminatorFilter(section, engine),
  ]);
}

/** The section's pinned sub-type in the engine's own field, or `always()` for a whole collection. */
export function discriminatorFilter(section: SearchSection, engine: SearchEngine): Predicate<SearchDocument> {
  if (section.orderType !== undefined) return ORDER_OF_TYPE(engine, section.orderType);
  return section.discriminator
    ? eq<SearchDocument>(section.discriminator.field, section.discriminator.value)
    : always<SearchDocument>();
}

/** Union the `is:open`/`is:closed` status sets for this section into one clause. */
function buildLifecycleFilter(section: SearchSection, filters: readonly Filter[]): Predicate<SearchDocument> {
  const lifecycle = filters.filter((f): f is Extract<Filter, { dim: 'lifecycle' }> => f.dim === 'lifecycle');
  if (lifecycle.length === 0 || !section.statusOpenState) return always();

  const wantOpen = lifecycle.some((f) => f.open);
  const wantClosed = lifecycle.some((f) => !f.open);
  // Open before closed keeps the clause deterministic when both are applied.
  const set = [
    ...(wantOpen ? section.statusOpenState.open : []),
    ...(wantClosed ? section.statusOpenState.closed : []),
  ];
  return set.length > 0 ? inList<SearchDocument>('status', set) : always();
}

/** Entitled, render-capable sections for the all-view candidate set. */
export function entitledRenderableSections(collections: readonly string[]): SearchSection[] {
  return getEntitledSections(collections).filter((section) => getCollectionConfig(section.collection));
}
